// ABOUTME: Helpers of the Actions step: a fresh action of each kind, completeness, duplicated slots and the
// ABOUTME: expansion of the "all text fields" replace into one replaceText action per field (a translate wildcard is sent as is).

import {
    ACTION_TYPES,
    BulkChangeAction,
    BulkChangeActionKind,
    ContentTypeRef,
    EligibleContentType,
    FieldAction,
} from '../types';
import { buildFindPattern, isFindPatternError } from './buildFindPattern';

/**
 * ctg/ct of a replaceText action that targets every editable text and html field, or of a translate
 * action on every multilingual one (expanded by the server).
 */
export const ALL_TEXT_FIELDS = '*';

/** False for a delete, which has no field. */
export const isAllTextFields = (action: ContentTypeRef | BulkChangeAction) =>
    'ctg' in action && action.ctg === ALL_TEXT_FIELDS && action.ct === ALL_TEXT_FIELDS;

/** A new action of `kind`, keeping the field and languages of `previous` when they still fit. */
export function defaultAction(
    kind: BulkChangeActionKind,
    previous?: BulkChangeAction,
    previousType?: string,
): BulkChangeAction {
    // Unlinking the linking objects keeps more data than deleting them.
    if (kind === 'deleteNode') return { kind, linkedAction: 'unlink' };
    const previousField = previous?.kind === 'deleteNode' ? undefined : previous;
    const keepsField =
        !!previousField &&
        !isAllTextFields(previousField) &&
        !!previousType &&
        ACTION_TYPES[kind].includes(previousType);
    const base = {
        ctg: keepsField ? previousField.ctg : '',
        ct: keepsField ? previousField.ct : '',
        ...(previousField?.locales?.length ? { locales: previousField.locales } : {}),
    };
    switch (kind) {
        case 'replaceText':
            return {
                ...base,
                kind,
                find: '',
                replace: '',
                isRegex: false,
                caseSensitive: false,
                wholeWord: false,
            };
        case 'setValue':
            return {
                ...base,
                kind,
                value: '',
                writeMode: 'onlyIfEmpty',
                inheritedValues: 'filled',
            };
        case 'clearValue':
            return { ...base, kind };
        case 'copyValue':
            return {
                ...base,
                ctg: '',
                ct: '',
                kind,
                from: { ctg: base.ctg, ct: base.ct },
                writeMode: 'onlyIfEmpty',
                inheritedValues: 'filled',
            };
        case 'addLinks':
        case 'removeLinks':
            return { ...base, kind, nodeIds: [] };
        case 'translate':
            // The translate targets are their own list: the previous languages are not kept.
            return { kind, ctg: ALL_TEXT_FIELDS, ct: ALL_TEXT_FIELDS, locales: [] };
        default:
            return { ...base, kind, fromNodeId: '', toNodeId: '' };
    }
}

const hasValue = (value: unknown): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
        return hasValue((value as { value: unknown }).value);
    }
    return true;
};

/** The action has everything the server needs (field, value, pattern...). */
export function isActionComplete(action: BulkChangeAction): boolean {
    // A delete has no field to pick: its linkedAction always has a value.
    if (action.kind === 'deleteNode') return true;
    if (!action.ctg || !action.ct) return false;
    switch (action.kind) {
        case 'replaceText':
            return !isFindPatternError(buildFindPattern(action));
        case 'setValue':
            return hasValue(action.value);
        case 'copyValue':
            return !!action.from.ctg && !!action.from.ct;
        case 'addLinks':
        case 'removeLinks':
            return action.nodeIds.length > 0;
        case 'replaceLink':
            return !!action.fromNodeId && !!action.toNodeId;
        case 'translate':
            return action.locales.length > 0;
        default:
            return true;
    }
}

/** The languages an action writes; empty means every language. */
const targetLangs = (action: FieldAction): string[] => {
    if (action.kind === 'copyValue' && action.toLang) return [action.toLang];
    return action.locales ?? [];
};

const overlaps = (a: string[], b: string[]) =>
    !a.length || !b.length || a.some(lang => b.includes(lang));

/**
 * The indexes of the actions that write the same field and language as another one: the server
 * rejects them, so that the order of the actions never matters.
 */
export function findDuplicateSlots(actions: BulkChangeAction[]): Set<number> {
    const duplicates = new Set<number>();
    actions.forEach((action, index) => {
        if (action.kind === 'deleteNode' || !action.ctg || !action.ct) return;
        actions.slice(index + 1).forEach((other, offset) => {
            if (
                other.kind !== 'deleteNode' &&
                (isAllTextFields(action) || isAllTextFields(other)
                    ? action.kind === other.kind &&
                      isAllTextFields(action) === isAllTextFields(other)
                    : other.ctg === action.ctg && other.ct === action.ct) &&
                overlaps(targetLangs(action), targetLangs(other))
            ) {
                duplicates.add(index);
                duplicates.add(index + 1 + offset);
            }
        });
    });
    return duplicates;
}

/**
 * The actions sent to the server: an "all text fields" replace becomes one replaceText per
 * editable text/html field that no other action writes. A translate wildcard is expanded by the
 * server.
 */
export function expandActions(
    actions: BulkChangeAction[],
    contentTypes: EligibleContentType[],
): BulkChangeAction[] {
    const taken = new Set(
        actions.flatMap(action =>
            action.kind === 'deleteNode' || isAllTextFields(action)
                ? []
                : [`${action.ctg}.${action.ct}`],
        ),
    );
    return actions.flatMap(action => {
        if (!isAllTextFields(action) || action.kind !== 'replaceText') return [action];
        return contentTypes
            .filter(
                contentType =>
                    contentType.editable &&
                    ACTION_TYPES.replaceText.includes(contentType.type) &&
                    !taken.has(`${contentType.ctg}.${contentType.ct}`),
            )
            .map(contentType => ({ ...action, ctg: contentType.ctg, ct: contentType.ct }));
    });
}
